import { clasificarBradenTotal, clasificarDowntonTotal } from './escalasEvolucion';

// Riesgos con que VIENE el paciente: LPP (Braden) y caídas (Downton), al
// recibir. Dictado del fundador (2026-09-10): "en Bupa las escalas las hace el
// turno de noche, así que el turno largo recibe al paciente con las escalas
// hechas. No debería conocer al paciente para saber qué riesgo tiene: es como
// los dispositivos, sin conocerlo ya sabemos en qué día está. Y si quieren
// hacer la escala, dejar que se despliegue".
//
// Por eso el dato de F1 es el PUNTAJE que trae (un número), clasificado con
// los MISMOS cortes de la escala (`clasificarBradenTotal`,
// `clasificarDowntonTotal`, fuente única), y con `fuente` que dice si vino
// recibido o lo calculó la enfermera. Catálogo cerrado, cero texto: es lo que
// el tablero de la jefatura puede leer sin ver al paciente.
//
// Vive en `registro.riesgos` (mismo nivel que `registro.dispositivos`): es un
// hecho de la recepción, no de la evolución. Clave opcional: un registro sin
// ella se comporta igual que antes.

// AMPLIADO el 2026-09-10 (pedido del fundador: "los riesgos, permitir colocar
// directamente si es alto moderado o bajo. y en el detalle si es que se quiere
// el puntaje"): en la entrega real te dicen "viene con riesgo alto de caídas",
// no el número. Así que el NIVEL es lo primero y el puntaje pasa a ser el
// detalle opcional. Los dos siguen siendo el MISMO dato: con puntaje el nivel
// se deriva de los cortes de la escala, y sin él se declara directo.
//
// `niveles` es el catálogo cerrado de cada escala (Downton no tiene moderado:
// es alto o bajo) y `clasificacionPorNivel` la frase EXACTA que ya usan
// `clasificarBradenTotal` / `clasificarDowntonTotal`, para que la nota escrita
// desde un nivel y la escrita desde un puntaje sean reconocibles por la misma
// función (`identificarNotaRiesgo`) y el bloque "Seguridad." de la evolución no
// tenga que aprender un segundo vocabulario.
export const RIESGOS = [
  {
    id: 'lpp', label: 'Riesgo de LPP', escala: 'braden', min: 6, max: 23,
    ayuda: 'Braden, de 6 a 23. Menor puntaje, más riesgo. Cortes de la NT 234 del MINSAL: 12 o menos alto, 13 a 15 moderado, 16 o más bajo.',
    niveles: ['alto', 'moderado', 'bajo'],
    clasificacionPorNivel: { alto: 'Riesgo alto', moderado: 'Riesgo moderado', bajo: 'Riesgo bajo' },
  },
  {
    id: 'caidas', label: 'Riesgo de caídas', escala: 'downton', min: 0, max: 11,
    ayuda: 'Downton, de 0 a 11. 3 o más es riesgo alto.',
    niveles: ['alto', 'bajo'],
    clasificacionPorNivel: { alto: 'Riesgo alto de caídas', bajo: 'Riesgo bajo de caídas' },
  },
];

export const FUENTES_RIESGO = ['recibido', 'calculado'];

// Nivel de tres escalones para el tablero (alto / moderado / bajo). Colapsa el
// vocabulario propio de cada escala a lo que la jefatura pregunta ("cuántos
// riesgo alto, moderado y bajo hay"). Downton no tiene moderado: es alto o bajo.
// "Riesgo muy alto" y "Sin riesgo" ya no los produce clasificarBradenTotal
// (cortes de la NT 234 desde el 2026-10-10); se siguen reconociendo por si
// llega un valor escrito con los cortes anteriores.
const NIVEL_POR_CLASIFICACION = {
  'Riesgo muy alto': 'alto',
  'Riesgo alto': 'alto',
  'Riesgo moderado': 'moderado',
  'Riesgo bajo': 'bajo',
  'Sin riesgo': 'bajo',
  'Riesgo alto de caídas': 'alto',
  'Riesgo bajo de caídas': 'bajo',
};
export const NIVELES_RIESGO = ['alto', 'moderado', 'bajo'];

const CLASIFICADOR = { braden: clasificarBradenTotal, downton: clasificarDowntonTotal };

/**
 * Clasifica un puntaje con los cortes de su escala.
 * @returns {{ puntaje:number, nivel:'alto'|'moderado'|'bajo', clasificacion:string, label:string } | null}
 */
export function clasificarRiesgo(id, puntaje) {
  const def = RIESGOS.find((r) => r.id === id);
  if (!def) return null;
  const c = CLASIFICADOR[def.escala]?.(puntaje);
  if (!c) return null;
  return { puntaje: Number(puntaje), nivel: NIVEL_POR_CLASIFICACION[c.clasificacion] || 'bajo', clasificacion: c.clasificacion, label: c.label };
}

/**
 * Clasifica un NIVEL declarado directo (sin puntaje). Un nivel que no existe en
 * esa escala (el 'moderado' de Downton, o cualquier basura de un jsonb
 * adulterado) devuelve null: jamás se afirma un riesgo que la escala no tiene.
 * @returns {{ puntaje:null, nivel:string, clasificacion:string, label:null } | null}
 */
export function clasificarNivel(id, nivel) {
  const def = RIESGOS.find((r) => r.id === id);
  if (!def || !def.niveles.includes(nivel)) return null;
  return { puntaje: null, nivel, clasificacion: def.clasificacionPorNivel[nivel], label: null };
}

/**
 * Escribe (o borra con valor null) un riesgo en el registro. PURA.
 * `valor` es un PUNTAJE (número) o un NIVEL (`{ nivel }`). Nunca ambos a la vez:
 * la ÚLTIMA acción del usuario manda, así que guardar un puntaje borra el nivel
 * declarado y declarar un nivel borra el puntaje. Un valor inválido no toca el
 * registro (misma regla de siempre).
 */
export function conRiesgo(registro, id, valor, fuente = 'recibido') {
  const base = registro || {};
  const riesgos = { ...(base.riesgos || {}) };
  if (valor === null || valor === undefined || valor === '') {
    delete riesgos[id];
    return { ...base, riesgos };
  }
  const conFuente = FUENTES_RIESGO.includes(fuente) ? fuente : 'recibido';
  if (typeof valor === 'object') {
    const n = clasificarNivel(id, valor.nivel);
    if (!n) return base;
    riesgos[id] = { nivel: n.nivel, fuente: conFuente };
    return { ...base, riesgos };
  }
  const c = clasificarRiesgo(id, valor);
  if (!c) return base;
  riesgos[id] = { puntaje: c.puntaje, fuente: conFuente };
  return { ...base, riesgos };
}

/** Lee un riesgo re-clasificando al leer: un jsonb adulterado jamás afirma un nivel. */
export function riesgoDe(registro, id) {
  const r = registro?.riesgos?.[id];
  if (!r) return null;
  // El puntaje manda cuando está: es el dato más fino de los dos.
  const c = clasificarRiesgo(id, r.puntaje) || clasificarNivel(id, r.nivel);
  return c ? { ...c, fuente: FUENTES_RIESGO.includes(r.fuente) ? r.fuente : 'recibido' } : null;
}

// La línea que entra a la cronología al guardar (mismo estilo que la nota del
// Downton calculado de F1: puntaje + clasificación, jamás la conducta).
// Con un nivel declarado directo no hay puntaje que decir, así que la nota es
// "Braden: riesgo alto" — el mismo hecho pelado, sin inventar un número.
export function notaRiesgo(id, valor, fuente = 'recibido') {
  const def = RIESGOS.find((r) => r.id === id);
  if (!def) return null;
  const nombre = def.escala === 'braden' ? 'Braden' : 'Downton';
  const cola = fuente === 'recibido' ? ' (recibido del turno anterior)' : '';
  if (valor !== null && typeof valor === 'object') {
    const n = clasificarNivel(id, valor.nivel);
    if (!n) return null;
    const clas = n.clasificacion.charAt(0).toLocaleLowerCase() + n.clasificacion.slice(1);
    return `${nombre}: ${clas}${cola}`;
  }
  const c = clasificarRiesgo(id, valor);
  if (!c) return null;
  return `${nombre} ${c.label}: ${c.clasificacion}${cola}`;
}

/**
 * Resumen para el tablero: cuántas camas por nivel y cuáles, por riesgo.
 * `camas` = [{ cama, riesgos: { lpp: {nivel}, caidas: {nivel} } }].
 */
export function resumenRiesgos(camas = []) {
  const out = {};
  for (const def of RIESGOS) {
    const porNivel = { alto: [], moderado: [], bajo: [] };
    let sinDato = 0;
    for (const c of camas) {
      const r = c?.riesgos?.[def.id];
      if (r && porNivel[r.nivel]) porNivel[r.nivel].push(c.cama);
      else sinDato += 1;
    }
    out[def.id] = { label: def.label, alto: porNivel.alto, moderado: porNivel.moderado, bajo: porNivel.bajo, sinDato };
  }
  return out;
}

// Reconoce únicamente la frase de catálogo escrita por la recepción. Las
// notas libres y mediciones con contexto adicional no se sustituyen.
// Clasificación de Braden con los cortes ANTERIORES a la NT 234 (vigentes en
// la app hasta el 2026-10-10). Sirve SOLO para reconocer una nota que se
// escribió con ellos, que se sigue leyendo tal como quedó escrita: jamás para
// clasificar un puntaje nuevo.
function clasificacionBradenAnterior(t) {
  return t <= 9 ? 'Riesgo muy alto' : t <= 12 ? 'Riesgo alto' : t <= 14 ? 'Riesgo moderado' : t <= 18 ? 'Riesgo bajo' : 'Sin riesgo';
}

export function identificarNotaRiesgo(texto) {
  const limpia = t => String(t || '').trim().replace(/\.$/, '').toLocaleLowerCase();
  for (const def of RIESGOS) {
    for (let puntaje = def.min; puntaje <= def.max; puntaje += 1) {
      for (const fuente of FUENTES_RIESGO) {
        if (limpia(texto) === limpia(notaRiesgo(def.id, puntaje, fuente))) return { id: def.id, puntaje, fuente };
        if (def.escala === 'braden') {
          const cola = fuente === 'recibido' ? ' (recibido del turno anterior)' : '';
          const anterior = `Braden ${puntaje}/23: ${clasificacionBradenAnterior(puntaje)}${cola}`;
          if (limpia(texto) === limpia(anterior)) return { id: def.id, puntaje, fuente, cortes: 'anteriores' };
        }
      }
    }
    // Y las notas escritas desde un NIVEL declarado directo (2026-09-10): son
    // del mismo catálogo cerrado, así que se reconocen igual y el bloque
    // "Seguridad." de la evolución las trata como a las de puntaje.
    for (const nivel of def.niveles) {
      for (const fuente of FUENTES_RIESGO) {
        if (limpia(texto) === limpia(notaRiesgo(def.id, { nivel }, fuente))) return { id: def.id, nivel, fuente };
      }
    }
  }
  return null;
}

// Urgencia de cada nivel para el color de lo seleccionado (tres niveles,
// lib/tonoClinico.js): un riesgo alto se pinta rojo solo cuando se eligió.
export const TONO_NIVEL_RIESGO = { alto: 'critico', moderado: 'atencion', bajo: 'estable' };
